import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { datasetName, perspective } from "../config";
import { IChat, IChatbotState } from "../types/chatbot";
import { aiClient, catalogClient, competitorClient } from "../utils/http";
import { nanoid } from "nanoid";
import { fetchChats } from "../components/Navbar/fetchData";
import Loader from '../components/Loader/loader';
import { getDivisionName } from "../components/Profile/division";
import { enqueueSnackbar } from "notistack";
import { useMediaQuery, useTheme } from "@mui/material";

const ChatbotContext = createContext<IChatbotState | null>(null);

export function ChatbotProvider({
  children,
  tool,
}: PropsWithChildren<{ tool: string }>) {
  const conversationsContainerRef = useRef<HTMLDivElement>();
  const [selectedChat, setSelectedChat] = useState(0);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isChatLoading, setIsChatLoading] = useState<boolean>(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [chats, setChats] = useState<IChat[]>([
    {
      sessionId: nanoid(),
      title: "New chat",
      conversations: [],
    },
  ]);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  useEffect(() => {
    const loadChats = async () => {
      setLoading(true);
      try {
        const fetchedChats = await fetchChats();
        setChats(fetchedChats);
        setSelectedChat(fetchedChats.length - 1);
        setLoading(false);
      } catch (error) {
        console.error("Error loading chats:", error);
        setLoading(false);
        enqueueSnackbar("Unable to load chats at this time. Please refresh the page or log in again.", {
          variant: 'error',
          autoHideDuration: 10000,
          anchorOrigin: { vertical: isSmallScreen ? 'bottom' : 'top', horizontal: 'center' }
        });
      }
    };

    if (!hasFetched.current) {
      loadChats();
      hasFetched.current = true;
    }
  }, []);

  useEffect(() => {
    // auto select on new chat create
    scrollDown();
  }, [chats]);

  useEffect(() => {
    scrollDown();
  }, [selectedChat]);

  function scrollDown() {
    const container = conversationsContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 300);
    }
  }

  // function getClient() {
  //   return tool === "ai" ? aiClient : competitorClient;
  // }
  function getClient() {
    return tool === "ai"
      ? aiClient
      : tool === "competitor"
        ? competitorClient
        : catalogClient;
  }

  useEffect(() => {
    if (tool !== "ai" && tool !== "catalog") {
      getClient()
        .post("/suggestion", {
          datasetName: datasetName,
          perspective: perspective,
        })
        .then(({ data }) => {
          setSuggestions(data.questionSuggestion);
        })
        .catch((e) => {
          console.error(e);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function newChat() {
    await setChats([
      ...chats,
      {
        sessionId: nanoid(),
        title: "New chat",
        conversations: [],
      },
    ]);
    setSelectedChat(chats.length);
  }

  function addConversation(query: string, locale: string) {
    setChats((chats) => {
      chats[selectedChat].conversations.push({
        query: query,
        locale: locale,
        isLoading: true,
      });
      return [...chats];
    });
    setTimeout(() => {
      fetchAnswer(chats[selectedChat].conversations.length - 1);
    }, 100);
  }

  function like(chatIndex: number) {
    setChats((chats) => {
      chats[selectedChat].conversations[chatIndex].isLiked = true;
      return [...chats];
    });
  }

  function dislike(chatIndex: number) {
    setChats((chats) => {
      chats[selectedChat].conversations[chatIndex].isLiked = false;
      return [...chats];
    });
  }


  function fetchAnswer(conversationIndex: number) {
    setIsChatLoading(true);
    let previousConversation =
      chats[selectedChat].conversations[conversationIndex - 1];

    // const sessionId = sessionStorage?.getItem('sessionid');
    const userId = sessionStorage?.getItem('userId');
    const userDetailsString = sessionStorage.getItem('userDetails');
    const userDetailsJson = userDetailsString ? JSON.parse(userDetailsString) : null;
    const userDetailsObject = userDetailsJson ? userDetailsJson : {};
    const sanitizedQuery = chats[selectedChat].conversations[conversationIndex].query
      .replace(/\\\\n/g, '\n')
      .replace(/\n+/g, ' ');
    getClient().post(
      "/",
      {
        userId: userId?.toString(),
        user_query: sanitizedQuery,
        chat_id: chats[selectedChat].sessionId,
        user_details: {
          user_id: userDetailsObject?.emp_No,
          phone_number: userDetailsObject['Contact No'],
          division_code: userDetailsObject?.Division,
          division_name: getDivisionName(userDetailsObject?.Division),
          location: userDetailsObject?.Location,
          name: userDetailsObject?.Name,
          email_id: userDetailsObject?.Email,
          role: userDetailsObject?.Role,
        }
      },
      {
        headers: {
          'sessionId': sessionStorage?.getItem('sessionid'),
        },
      }
    )
      .then(({ data }) => {
        // const responseBody = JSON.parse(data.body);
        const answer = data.answer;
        const answerId = data.id;
        const documents = data.document_urls;
        sessionStorage.setItem('LastResponseId', data.id);
        setIsChatLoading(false);
        setChats((chats) => {
          chats[selectedChat].conversations[conversationIndex].isLoading =
            false;
          chats[selectedChat].conversations[conversationIndex].sqlQuery =
            data.sql_query;
          chats[selectedChat].conversations[conversationIndex].result =
            answer;
          chats[selectedChat].conversations[conversationIndex].resultId =
            answerId;
          chats[selectedChat].conversations[conversationIndex].resultDocument =
            documents;
          chats[selectedChat].conversations[conversationIndex].isPlottable =
            data.is_plottable;
          chats[selectedChat].conversations[conversationIndex].message =
            data.message;
          chats[selectedChat].conversations[conversationIndex].displyType =
            data.displyType;
          chats[selectedChat].conversations[
            conversationIndex
          ].questionSuggestion = data.questionSuggestion;
          chats[selectedChat].conversations[conversationIndex].raw = data;
          return [...chats];
        });
      })
      .catch((e) => {
        setIsChatLoading(false);
        chats[selectedChat].conversations[conversationIndex].isLoading = false;
        chats[selectedChat].conversations[conversationIndex].result = "Something went wrong. Please try again!!!";
        chats[selectedChat].conversations[conversationIndex].isPlottable =
          false;
        console.error(e);
      });
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <ChatbotContext.Provider
      value={{
        selectedChat,
        setSelectedChat,
        chats,
        newChat,
        addConversation,
        conversationsContainerRef,
        suggestions,
        like,
        dislike,
        isChatLoading,
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
}

export default function useChatbot(): IChatbotState {
  let context = useContext(ChatbotContext);
  return context!;
}
